/**
 * Polígono de REFERENCIA del derecho minero (contorno oficial de INGEMMET) para el
 * editor de la concesión. El derecho es el "edificio" y la concesión del minero un
 * "departamento" adentro: al dibujar la concesión se muestra el derecho de fondo y
 * se avisa si algún vértice queda fuera.
 */
type LL = { lat: number; lng: number }

/** Anillos exteriores de un GeoJSON Polygon / MultiPolygon (o Feature con esa geometría). */
export function referenceRings(geoJson: string | null | undefined): LL[][] {
  if (!geoJson) return []
  try {
    let g = JSON.parse(geoJson)
    if (g?.type === 'Feature') g = g.geometry
    const polys: number[][][][] = g?.type === 'Polygon' ? [g.coordinates] : g?.type === 'MultiPolygon' ? g.coordinates : []
    return polys
      .map((p) => (p[0] ?? []).map(([lng, lat]) => ({ lat, lng })))
      .filter((ring) => ring.length >= 3)
  } catch {
    return []
  }
}

/** Punto dentro de un anillo (ray casting). */
function inRing(pt: LL, ring: LL[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]!
    const b = ring[j]!
    const cross = (a.lat > pt.lat) !== (b.lat > pt.lat)
      && pt.lng < ((b.lng - a.lng) * (pt.lat - a.lat)) / (b.lat - a.lat) + a.lng
    if (cross) inside = !inside
  }
  return inside
}

/** Distancia aproximada en metros de un punto al borde de un anillo. */
function metersToRing(pt: LL, ring: LL[]): number {
  const kLat = 111_320
  const kLng = 111_320 * Math.cos((pt.lat * Math.PI) / 180)
  let best = Infinity
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const ax = (ring[j]!.lng - pt.lng) * kLng, ay = (ring[j]!.lat - pt.lat) * kLat
    const bx = (ring[i]!.lng - pt.lng) * kLng, by = (ring[i]!.lat - pt.lat) * kLat
    const dx = bx - ax, dy = by - ay
    const len2 = dx * dx + dy * dy
    const t = len2 > 0 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2)) : 0
    const cx = ax + t * dx, cy = ay + t * dy
    best = Math.min(best, Math.hypot(cx, cy))
  }
  return best
}

/** Tolerancia al borde: los vértices se escriben en UTM entero (±1 m) y una concesión
 *  que coincide con el lindero del derecho no debe marcarse "fuera" por redondeo. */
const EDGE_TOLERANCE_M = 5

/** Cuántos puntos caen fuera de TODOS los anillos de referencia. */
export function countOutside(points: LL[], rings: LL[][]): number {
  if (rings.length === 0) return 0
  return points.filter((p) => !rings.some((r) => inRing(p, r) || metersToRing(p, r) <= EDGE_TOLERANCE_M)).length
}
